import { useLayoutEffect, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import seoMeta from '../data/seoMeta';

// The single owner of scroll behavior on navigation.
// - Forward (PUSH/REPLACE): jump to top instantly, before paint (no visible slide).
// - Back/forward (POP): restore the position the user was at (native browser feel).
// Positions are keyed by history entry (location.key), matching browser semantics.
const positions = new Map();

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'
  const announced = useRef(null);

  // Continuously remember where the user is on the current history entry.
  useEffect(() => {
    const key = location.key;
    const onScroll = () => positions.set(key, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key]);

  // Apply the scroll BEFORE the browser paints the new route (no one-frame flash).
  // behavior:'instant' + temporarily forcing scroll-behavior:auto defeats any CSS
  // smooth so route changes never animate.
  useLayoutEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    const top = navType === 'POP' ? (positions.get(location.key) ?? 0) : 0;
    window.scrollTo({ top, left: 0, behavior: 'instant' });
    html.style.scrollBehavior = prev;
  }, [location.key, navType]);

  // Accessibility: on forward navigation move focus to the main landmark
  // (preventScroll so it doesn't undo the jump), and announce the new page to
  // screen readers via a polite live region.
  useEffect(() => {
    if (navType !== 'POP') {
      const main = document.getElementById('main');
      if (main) main.focus({ preventScroll: true });
    }
    const node = document.getElementById('route-announcer');
    if (node) {
      const id = window.setTimeout(() => {
        const title = (seoMeta[location.pathname] && seoMeta[location.pathname].title) || document.title;
        if (title && title !== announced.current) {
          node.textContent = title;
          announced.current = title;
        }
      }, 90);
      return () => window.clearTimeout(id);
    }
  }, [location.key, location.pathname, navType]);

  return null;
}
